import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sun, MoonStars, ArrowRight } from "@phosphor-icons/react";
import { useTheme } from "@/contexts/ThemeContext";
import { LodgyLogo } from "@/components/LodgyLogo";
import EnvBadge from "@/components/EnvBadge";

export default function LoginPage() {
  const { user, login } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [requires2fa, setRequires2fa] = useState(false);
  const [totpCode, setTotpCode] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password, requires2fa ? totpCode : undefined);
    setLoading(false);
    if (res.ok) {
      navigate("/dashboard");
    } else if (res.requires2fa) {
      setRequires2fa(true);
      setTotpCode("");
      setError("");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-stone-900 p-4">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1565784623727-8cebf4712b2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxjcm9hdGlhJTIwYWRyaWF0aWMlMjBjb2FzdCUyMHZpbGxhfGVufDB8fHx8MTc4MjIxMjg5OHww&ixlib=rb-4.1.0&q=85)`,
        }}
      />
      <div className="absolute inset-0 bg-stone-950/65" />

      {/* Theme toggle */}
      <button
        onClick={toggle}
        className="absolute right-5 top-5 z-10 rounded-full bg-white/10 p-2 text-white backdrop-blur hover:bg-white/20"
        data-testid="login-theme-toggle"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun size={18} weight="duotone" /> : <MoonStars size={18} weight="duotone" />}
      </button>

      {/* Card */}
      <div className="relative z-10 w-full max-w-md" data-testid="login-card">
        <div className="mb-8 flex flex-col items-center text-center text-white">
          <LodgyLogo size={44} className="mb-3" />
          <div className="mt-2"><EnvBadge /></div>
          <p className="mt-1 text-sm text-white/75">
            Property Management System • Apartmani, hoteli, kampovi
          </p>
        </div>

        <form
          onSubmit={submit}
          className="space-y-4 rounded-2xl border border-white/10 bg-white/95 p-7 shadow-2xl backdrop-blur dark:bg-[#0F1A2B]/95 dark:border-white/5"
        >
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="ime@primjer.hr"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Lozinka</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="••••••••"
              data-testid="login-password-input"
            />
          </div>

          {requires2fa && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                2FA kod iz Authenticator aplikacije
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoFocus
                required
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full rounded-md border border-primary/50 bg-background px-3 py-2.5 text-center font-mono text-lg tracking-widest text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="123 456"
                data-testid="login-totp-input"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Otvori Google Authenticator (ili 1Password / Authy) i upiši 6-znamenkasti kod za <b>Lodgy PMS</b>.
              </p>
            </div>
          )}

          {error && (
            <div
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="group flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-50"
            data-testid="login-submit-btn"
          >
            {loading ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/70 border-t-transparent" />
            ) : (
              <>
                Prijava
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </button>

          <p className="pt-2 text-center text-xs text-muted-foreground">
            Nemate račun?{" "}
            <Link
              to="/register"
              className="font-semibold text-primary hover:underline"
              data-testid="login-register-link"
            >
              Registrirajte se
            </Link>
          </p>
        </form>

        <p className="mt-6 text-center text-xs text-white/50">
          © 2026 Lodgy.co — Property Management
        </p>
      </div>
    </div>
  );
}
